/**
 * Opened trades types - Updated to match new VybeOrder C# schema
 */

// Import enums and helper functions from trade history (same schema)
export {
  VybeOrderType,
  VybeDealType,
  VybeOrderState,
  VybePlacedType,
  VybeExpirationType,
  VybeFillPolicy,
  VybeDirection,
  formatOrderType,
  formatDealType,
  formatOrderState,
  formatPlacedType,
  formatExpirationType,
  formatFillPolicy,
  formatDirection
} from '../trade-history/types';

// Additional enums for real-time trade events
export enum VybeMsg {
  DONE = 0,
  OK = 1,
  COMMON_ERROR = 2,
  INVALID_PARAM = 3,
  INVALID_DATA = 4,
  DISK_ERROR = 5,
  MEMORY_ERROR = 6,
  NETWORK_ERROR = 7,
  NOT_PERMISSION = 8,
  OPERATION_TIMEOUT = 9,
  NO_CONNECTION = 0xA,
  SERVICE_NOT_AVAILABLE = 0xB,
  TOO_FREQUENT_REQUEST = 0xC,
  NOT_FOUND = 0xD,
  SERVER_SHUTDOWN = 0xF,
  OPERATION_CANCELED = 0x10,
  DUPLICATE_ATTEMPT = 0x11,
  REQUEST_ON_WAY = 0x2711,
  REQUEST_ACCEPTED = 0x2712,
  REQUEST_PROCESSED = 0x2713,
  REQUOTE = 0x2714,
  PRICES = 0x2715,
  REQUEST_REJECTED = 0x2716,
  REQUEST_CANCELLED = 0x2717,
  ORDER_PLACED = 0x2718,
  REQUEST_EXECUTED = 0x2719,
  REQUEST_EXECUTED_PARTIALLY = 0x271A,
  REQUEST_ERROR = 0x271B,
  REQUEST_TIMEOUT = 0x271C,
  INVALID_REQUEST = 0x271D,
  INVALID_VOLUME = 0x271E,
  INVALID_PRICE = 0x271F,
  INVALID_STOPS = 0x2720,
  TRADE_DISABLED = 0x2721,
  MARKET_CLOSED = 0x2722,
  NO_MONEY = 0x2723,
  PRICE_CHANGED = 0x2724,
  NO_PRICES = 0x2725,
  INVALID_EXPIRATION = 0x2726,
  ORDER_ALREADY_CHANGED = 0x2727,
  TOO_MANY_TRADE_REQUESTS = 0x2728,
  NO_CHANGES = 0x2729,
  AUTOTRADE_DISABLE_SERVER = 0x272A,
  AUTOTRADE_DISABLE_CLIENT = 0x272B,
  MODIFICATION_FAILED = 0x272D,
  UNSUPPORTED_FILLING_MODE = 0x272E,
  ORDER_CANCELLED = 0x2739,
  FIFO_CLOSE = 10045,
  HEDGE_PROHIBITED = 10046
}

export enum VybeTradeType {
  TradePrice = 0,
  RequestExecution = 1,
  InstantExecution = 2,
  MarketExecution = 3,
  ExchangeExecution = 4,
  SetOrder = 5,
  ModifyDeal = 6,
  ModifyOrder = 7,
  CancelOrder = 8,
  Transfer = 9,
  ClosePosition = 10
}

export enum VybeUpdateType {
  Unknown = 0,
  PendingClose = 1,
  MarketOpen = 2,
  PendingOpen = 3,
  MarketClose = 4,
  PartialClose = 5,
  Started = 6,
  Filled = 7,
  Cancelling = 8,
  MarketModify = 9,
  PendingModify = 10,
  OnStopLoss = 11,
  OnTakeProfit = 12,
  OnStopOut = 13,
  Balance = 14,
  Expired = 15,
  Rejected = 16,
  MarketCloseBy = 17
}

// Define nested object interfaces locally (same as trade history)
export interface VybeDealInternal {
  ticketNumber: number;
  id: string;
  login: number;
  historyTime: number;
  orderTicket: number;
  openTimeAsDateTime: string;
  openTime: number;
  symbol: string;
  type: number; // VybeDealType
  direction: number; // VybeDirection
  openPrice: number;
  price: number;
  stopLoss: number;
  takeProfit: number;
  lots: number;
  volume: number;
  profit: number;
  profitRate: number;
  volumeRate: number;
  commission: number;
  fee: number;
  swap: number;
  expertId: number;
  positionTicket: number;
  comment: string;
  contractSize: number;
  digits: number;
  moneyDigits: number;
  freeProfit: number;
  trailRounder: number;
  openTimeMs: number;
  placedType: number; // VybePlacedType
}

export interface VybeOrderInternal {
  ticketNumber: number;
  ticket: number;
  id: string;
  login: number;
  symbol: string;
  historyTime: number;
  openTime: number;
  expirationTime: number;
  executionTime: number;
  executionTimeAsDateTime: string;
  type: number; // VybeOrderType
  fillPolicy: number; // VybeFillPolicy
  expirationType: number; // VybeExpirationType
  placedType: number; // VybePlacedType
  openPrice: number;
  stopLimitPrice: number;
  price: number;
  stopLoss: number;
  takeProfit: number;
  lots: number;
  volume: number;
  requestLots: number;
  requestVolume: number;
  state: number; // VybeOrderState
  expertId: number;
  dealTicket: number;
  comment: string;
  contractSize: number;
  digits: number;
  baseDigits: number;
  profitRate: number;
  openTimeMs: number;
  openTimeMsAsDateTime: string;
  isAssociativeDealOrder: boolean;
  isTradeOrder: boolean;
  isLimitOrder: boolean;
  isStopOrder: boolean;
  isBuyOrder: boolean;
  isStopLimitOrder: boolean;
}

// Updated OpenedTrade interface to match new schema
export interface OpenedTrade {
  // Core identification
  ticket: number;
  symbol: string;
  
  // Financial data
  profit: number;
  swap: number;
  commission: number;
  fee: number;
  
  // Close data (for open trades: closePrice = current market price)
  closePrice: number;
  closeTime: string;        // "0001-01-01T00:00:00" for open trades
  closeLots: number;        // 0 for open trades
  closeVolume: number;      // 0 for open trades
  closeComment: string | null; // null for open trades
  
  // Open data  
  openPrice: number;
  openTime: string;
  lots: number;
  volume: number;
  contractSize: number;
  expertId: number;
  
  // Order classification (now numeric enums)
  placedType: number; // VybePlacedType
  orderType: number;  // VybeOrderType
  dealType: number;   // VybeDealType
  state: number;      // VybeOrderState
  
  // Stops and limits
  stopLoss: number;
  takeProfit: number;
  comment: string;
  
  // New fields from updated schema
  requestId: number;
  digits: number;
  profitRate: number;
  stopLimitPrice: number;
  expirationType: number; // VybeExpirationType
  expirationTime: string; // "0001-01-01T00:00:00" for open trades
  fillPolicy: number;     // VybeFillPolicy
  openTimestampUTC: number;
  closeTimestampUTC: number; // 0 for open trades
  
  // Complex nested objects (for open trades: dealInternalOut and orderInternal are null)
  dealInternalIn: VybeDealInternal | null;
  dealInternalOut: VybeDealInternal | null;  // null for open trades
  orderInternal: VybeOrderInternal | null;   // null for open trades
}
// Keep existing response wrapper
export interface OpenedTradesResponse {
  success: boolean;
  errorMessage: string | null;
  orders: OpenedTrade[];
  totalCount: number;
}

export interface OrderModifyResponse {
  success: boolean;
  errorMessage?: string | null;
  order?: OpenedTrade | null;
  ticket?: number;
  symbol?: string;
  previousStopLoss?: number;
  previousTakeProfit?: number;
  newStopLoss?: number;
  newTakeProfit?: number;
}

// Updated real-time event interfaces to match C# schema

// VybeTransactionInfo
export interface VybeTransactionInfo {
  updateId: number;
  action: number;
  ticketNumber: number;
  currency: string;
  id: number;
  s58: number; // VybeOrderType
  orderState: number; // VybeOrderState
  expirationType: number; // VybeExpirationType
  expirationTime: number;
  openPrice: number;
  orderPrice: number;
  stopLoss: number;
  takeProfit: number;
  volume: number;
}

// VybeTradeRequest
export interface VybeTradeRequest {
  requestId: number;
  tradeType: number; // VybeTradeType
  login: number;
  transferLogin: number;
  currency: string;
  lots: number;
  digits: number;
  orderTicket: number;
  expirationTime: number;
  orderType: number; // VybeOrderType
  fillPolicy: number; // VybeFillPolicy
  expirationType: number; // VybeExpirationType
  flags: number;
  placedType: number; // VybePlacedType
  price: number;
  orderPrice: number;
  stopLoss: number;
  takeProfit: number;
  deviation: number;
  expertId: number;
  comment: string;
  dealTicket: number;
  byCloseTicket: number;
}

// VybeTradeResult
export interface VybeTradeResult {
  status: number; // VybeMsg
  positionId: number;
  ticketNumber: number;
  volume: number;
  openPrice: number;
  bid: number;
  ask: number;
  last: number;
  comment: string;
}

// VybeOrderProgress (matches C# class exactly)
export interface OrderProgress {
  orderUpdate: VybeTransactionInfo;
  tradeRequest: VybeTradeRequest;
  tradeResult: VybeTradeResult;
}

// VybeOrderUpdate (matches C# class exactly)
export interface OrderUpdate {
  trans: VybeTransactionInfo | null;
  orderInternal: VybeOrderInternal | null;
  deal: VybeDealInternal | null;
  oppositeDeal: VybeDealInternal | null;
  order: OpenedTrade | null; // This is the complete VybeOrder object when MT5 provides it.
  remainingOrder?: OpenedTrade | null;
  type: number; // VybeUpdateType
  closeByTicket: number;
}

export interface OpenedTradesState {
  // High-performance lookups
  byTicket: Map<number, OpenedTrade>;
  bySymbol: Map<string, OpenedTrade[]>;
  
  // Loading states
  loadingStatus: 'idle' | 'loading' | 'complete' | 'error';
  errorMessage: string | null;
  lastUpdated: Date | null;
  
  // Computed arrays (expensive - compute only when complete)
  allTrades: OpenedTrade[];
  totalCount: number;
}

export interface OpenedTradesActions {
  processOpenedTrades: (data: OpenedTradesResponse) => void;
  processOrderProgress: (data: OrderProgress) => void;
  processOrderUpdate: (data: OrderUpdate) => void;
  processOrderModify: (data: OrderModifyResponse) => void;
  reset: () => void;
  getTradeByTicket: (ticket: number) => OpenedTrade | undefined;
  getTradesBySymbol: (symbol: string) => OpenedTrade[];
}

export type OpenedTradesStore = OpenedTradesState & OpenedTradesActions;
// Formatter functions for new enums
export const formatTradeStatus = (status: number): string => {
  const statusMap: Record<number, string> = {
    [VybeMsg.DONE]: 'Done',
    [VybeMsg.OK]: 'OK',
    [VybeMsg.REQUEST_ON_WAY]: 'Request On Way',
    [VybeMsg.REQUEST_ACCEPTED]: 'Request Accepted',
    [VybeMsg.REQUEST_PROCESSED]: 'Request Processed',
    [VybeMsg.REQUOTE]: 'Requote',
    [VybeMsg.PRICES]: 'Prices',
    [VybeMsg.REQUEST_REJECTED]: 'Request Rejected',
    [VybeMsg.REQUEST_CANCELLED]: 'Request Cancelled',
    [VybeMsg.ORDER_PLACED]: 'Order Placed',
    [VybeMsg.REQUEST_EXECUTED]: 'Request Executed',
    [VybeMsg.REQUEST_EXECUTED_PARTIALLY]: 'Request Executed Partially',
    [VybeMsg.REQUEST_ERROR]: 'Request Error',
    [VybeMsg.REQUEST_TIMEOUT]: 'Request Timeout',
    [VybeMsg.INVALID_REQUEST]: 'Invalid Request',
    [VybeMsg.INVALID_VOLUME]: 'Invalid Volume',
    [VybeMsg.INVALID_PRICE]: 'Invalid Price',
    [VybeMsg.INVALID_STOPS]: 'Invalid Stops',
    [VybeMsg.TRADE_DISABLED]: 'Trade Disabled',
    [VybeMsg.MARKET_CLOSED]: 'Market Closed',
    [VybeMsg.NO_MONEY]: 'No Money',
    [VybeMsg.PRICE_CHANGED]: 'Price Changed',
    [VybeMsg.NO_PRICES]: 'No Prices',
    [VybeMsg.INVALID_EXPIRATION]: 'Invalid Expiration',
    [VybeMsg.ORDER_ALREADY_CHANGED]: 'Order Already Changed',
    [VybeMsg.TOO_MANY_TRADE_REQUESTS]: 'Too Many Trade Requests',
    [VybeMsg.NO_CHANGES]: 'No Changes',
    [VybeMsg.AUTOTRADE_DISABLE_SERVER]: 'Autotrade Disabled (Server)',
    [VybeMsg.AUTOTRADE_DISABLE_CLIENT]: 'Autotrade Disabled (Client)',
    [VybeMsg.MODIFICATION_FAILED]: 'Modification Failed',
    [VybeMsg.UNSUPPORTED_FILLING_MODE]: 'Unsupported Filling Mode',
    [VybeMsg.ORDER_CANCELLED]: 'Order Cancelled',
    [VybeMsg.FIFO_CLOSE]: 'FIFO Close',
    [VybeMsg.HEDGE_PROHIBITED]: 'Hedge Prohibited'
  };
  return statusMap[status] || `Status ${status}`;
};

export const formatTradeType = (tradeType: number): string => {
  const typeMap: Record<number, string> = {
    [VybeTradeType.TradePrice]: 'Trade Price',
    [VybeTradeType.RequestExecution]: 'Request Execution',
    [VybeTradeType.InstantExecution]: 'Instant Execution',
    [VybeTradeType.MarketExecution]: 'Market Execution',
    [VybeTradeType.ExchangeExecution]: 'Exchange Execution',
    [VybeTradeType.SetOrder]: 'Set Order',
    [VybeTradeType.ModifyDeal]: 'Modify Deal',
    [VybeTradeType.ModifyOrder]: 'Modify Order',
    [VybeTradeType.CancelOrder]: 'Cancel Order',
    [VybeTradeType.Transfer]: 'Transfer',
    [VybeTradeType.ClosePosition]: 'Close Position'
  };
  return typeMap[tradeType] || `Trade Type ${tradeType}`;
};

export const formatUpdateType = (updateType: number): string => {
  const typeMap: Record<number, string> = {
    [VybeUpdateType.Unknown]: 'Unknown',
    [VybeUpdateType.PendingClose]: 'Pending Close',
    [VybeUpdateType.MarketOpen]: 'Market Open',
    [VybeUpdateType.PendingOpen]: 'Pending Open',
    [VybeUpdateType.MarketClose]: 'Market Close',
    [VybeUpdateType.PartialClose]: 'Partial Close',
    [VybeUpdateType.Started]: 'Started',
    [VybeUpdateType.Filled]: 'Filled',
    [VybeUpdateType.Cancelling]: 'Cancelling',
    [VybeUpdateType.MarketModify]: 'Market Modify',
    [VybeUpdateType.PendingModify]: 'Pending Modify',
    [VybeUpdateType.OnStopLoss]: 'On Stop Loss',
    [VybeUpdateType.OnTakeProfit]: 'On Take Profit',
    [VybeUpdateType.OnStopOut]: 'On Stop Out',
    [VybeUpdateType.Balance]: 'Balance',
    [VybeUpdateType.Expired]: 'Expired',
    [VybeUpdateType.Rejected]: 'Rejected',
    [VybeUpdateType.MarketCloseBy]: 'Market Close By'
  };
  return typeMap[updateType] || `Update Type ${updateType}`;
};